/* global G */
import clean from 'app/_shared/hook/action/clean'
import appBarBack from 'app/_shared/hook/appbar/back'
import create from 'app/_shared/hook/appbar/create'
import multiCreate from 'app/_shared/hook/appbar/multiCreate'
import quickActions, { componentQuickActions } from 'app/_shared/hook/appbar/quickActions'
import search from 'app/_shared/hook/appbar/search'
import check from 'app/_shared/hook/check'
import recall from 'app/_shared/hook/component/recall'
import reset from 'app/_shared/hook/component/reset'
import cacheTitle from 'app/_shared/hook/model/cacheTitle'
import clear from 'app/_shared/hook/model/clear'
import mapAttribute from 'app/_shared/hook/model/mapAttribute'
import updateCache from 'app/_shared/hook/model/updateCache'
import onlineCheck from 'app/_shared/hook/onlineCheck'
import platform from 'app/_shared/hook/platform'
import back from 'app/_shared/hook/redirect/back'
import backOrRedirect from 'app/_shared/hook/redirect/backOrRedirect'
import stepper from 'app/_shared/hook/stepper'
import withApi from 'app/_shared/hook/withApi'
import recent from 'app/recent/hook/recent'
import submitFavorite from 'app/serviceItem/hook/submitFavorite'
import validateForm from 'app/serviceItem/hook/validateForm'
import bulk from 'lib/hook/model/bulk'
import read from 'lib/hook/model/read'
import submit from 'lib/hook/model/submit'
import validate from 'lib/hook/model/validate'
import action, { currentAction } from 'lib/hook/module/action'
import confirm from 'lib/hook/router/confirm'
import { begin, confirmation, end } from 'lib/hook/transaction'
import ui from 'lib/hook/ui/update'
import asObject from 'lib/sequence/component/children/asObject'
import { set } from 'lib/sequence/component/state/value'
import { setKey } from 'lib/util'

const _treeHooks = {
  before: [
  ],
  after: [
    platform({
      mobile: [appBarBack, componentQuickActions('right')],
    }),
    ui,
  ],
}

const _optionsHooks = {
  before: [
    obj => (...args) => {
      /**
       * Normally this would be done in the action logic, but because this
       * is GT-specific better do it here to have to copy fewer files.
       * (We need this hooks.js anyway)
       */
      const actionComponent = obj[G.STATE][G.ACTION][G.COMPONENT]
      const { equipmentOptions = [] } = obj[G.STATE]
      const { list } = asObject(actionComponent[G.CHILDREN])
      equipmentOptions.length && set(list, equipmentOptions.join('\n'))

      return args
    },
  ],
  after: [
    ui,
  ],
}

const _ownHooks = {
  after: [
    platform({
      web: [create],
      mobile: [multiCreate],
    }),
    recall,
    ui,
  ],
}

const _searchAndCreateHooks = {
  before: [
    module => (...args) => {
      // TODO: probably we shouldn't need this here
      // clearing model data that may previously have been set by new action
      const model = module[G.MODEL]
      model[G.DATA].equipment = []
      model[G.DATA].product = []
      return args
    },
    platform({
      web: [create, search],
      mobile: [multiCreate],
    }),
    recall,
  ],
  after: [
    ui,
  ],
}

const _createHooks = {
  before: [
    onlineCheck(),
    // Clears the module error, so that we can still use redirect to navigate the drawer actions.
    // Should be replaced by using force redirect
    obj => async (...args) => {
      setKey(null, G.ERROR, obj[G.STATE])
      return args
    },
    platform({
      mobile: [
        appBarBack,
        quickActions,
      ],
    }),
  ],
  after: [
    stepper,
    ui,
    confirm,
  ],
}

const _formHooks = {
  before: [
    onlineCheck(),
    reset,
  ],
  after: [
    ui,
    confirm,
  ],
}

const _requestsHooks = {
  before: [
    onlineCheck(),
    read,
    platform({
      mobile: create,
    }),
  ],
  after: [
    ui,
  ],
}

const _locationHooks = {
  before: [
    mapAttribute('installedAt'),
  ],
  after: [
    ui,
    confirm,
  ],
}

const _editHooks = {
  before: [
    onlineCheck(),
    platform({
      mobile: [quickActions],
    }),
    clear,
    reset,
    read,
  ],
  after: [
    ui,
    confirm,
  ],
}

const _detailHooks = {
  before: [
    withApi('device')({
      clear,
      read,
    }),
    cacheTitle('name', 'serial'),
    recall,
    recent,
    platform({
      mobile: [quickActions, multiCreate],
    }),
  ],
  after: [
    ui,
  ],
}

const _submitHooks = {
  before: [
    validateForm,
  ],
  after: [
    ui,
    check,
    begin,
    submit,
    updateCache,
    // confirmation('success'),
    clear,
    back,
    end,
  ],
}

const _bulkHooks = {
  before: [
    validate,
  ],
  after: [
    ui,
    // NOTE: The type changed to `device`. However this doesn't work for bulk operations.
    // We need to revert to `serviceitem` for those.
    bulk,
    submitFavorite,
    clean,
    obj => (...args) => {
      const history = obj[G.ADAPTER][G.SESSION][G.STATE][G.PREV] || []
      const lastEntry = history[history.length - 1]

      const { syntheticKey } = obj[G.MODEL][G.STATE]

      if (syntheticKey) {
        const settingsAdapter = obj[G.ADAPTER][G.SETTINGS]
        settingsAdapter[G.API].setRecent({
          key: syntheticKey,
          newKey: obj[G.MODEL][G.STATE][G.REF],
        })
      }

      // if we are coming from serviceItem/detail we just registered a device.
      // Set the correct `key` so we don't display the synthetic SI again
      if (lastEntry[G.MODULE] === 'serviceItem' && lastEntry[G.ACTION] === 'detail') {
        lastEntry[G.REF] === syntheticKey && (lastEntry[G.REF] = obj[G.MODEL][G.STATE][G.REF])
      }

      return args
    },
    backOrRedirect,
  ],
}

const _submitFavoriteHooks = {
  before: [],
  after: [
    ui,
    check,
    confirmation('favorite', 'success'),
    action('successFavorite'),
  ],
}

const _successFavoriteHooks = {
  before: [],
  after: [
    currentAction,
  ],
}

const hooks = {
  [G.ACTIONS]: {
    index: _searchAndCreateHooks,
    own: _ownHooks,
    new: _formHooks,
    create: _createHooks,
    detail: _detailHooks,
    location: _locationHooks,
    productDocumentation: _treeHooks,
    equipmentDocumentation: _treeHooks,
    equipmentOptions: _optionsHooks,
    edit: _editHooks,
    submit: _submitHooks,
    bulk: _bulkHooks,
    requests: _requestsHooks,

    submitFavorite: _submitFavoriteHooks,
    successFavorite: _successFavoriteHooks,
  },
}

export default hooks
